import { Radio } from 'lucide-react';
import { Button, Switch, Skeleton } from '@vnetwork/ui/primitives';
import { ActionBar, Container, PageHeader } from '@vnetwork/ui/layout';
import { BasicSection } from '../../event-jobs/event-job-create/BasicSection';
import { ProtectionSection } from '../../event-jobs/event-job-create/ProtectionSection';
import { RecordingSnapshotSection } from '../../event-jobs/event-job-create/RecordingSnapshotSection';
import { ResultSection } from '../../event-jobs/event-job-create/ResultSection';
import { LinearStreamConfigSection } from './LinearStreamConfigSection';
import { LinearConfigSection } from './LinearConfigSection';
import { LogoWatermarkSection } from './LogoWatermarkSection';
import { useLinearJobForm, canUseDashAdvancedTimeShift, DASH_RECORDING_PROTOCOLS } from './use-linear-job-form';
import type { CreateEventJobRequest } from '@/api/media/live-event-jobs-types';
import styles from './LinearJobCreate.module.css';

export interface LiveLinearJobCreateProps {
  serviceId: string;
}

/**
 * Create Linear (24/7) Job wizard — reuses BasicSection, ProtectionSection,
 * RecordingSnapshotSection, ResultSection from event-job-create; adds
 * LinearStreamConfigSection (expanded protocol list), LinearConfigSection
 * (MPEGTS/RTP track config + HA pairs + DAI), and LogoWatermarkSection.
 */
export function LiveLinearJobCreate({ serviceId }: LiveLinearJobCreateProps) {
  const {
    navigate,
    config,
    isConfigLoading,
    form,
    updateForm,
    updateLinearConfig,
    audioOnlyFormat,
    setAudioOnlyFormat,
    hlsProtection,
    setHlsProtection,
    dashProtection,
    setDashProtection,
    recording,
    setRecording,
    snapshot,
    setSnapshot,
    logo,
    setLogo,
    errors,
    clearError,
    createdJob,
    isSubmitting,
    handleSubmit,
  } = useLinearJobForm(serviceId);

  // DASH recording mode override for SRT/MPEGTS protocols
  const isDashRecordingProtocol = DASH_RECORDING_PROTOCOLS.includes(form.input_protocol);
  const recordingModes = isDashRecordingProtocol
    ? ([{ value: 'dash' as const, label: 'DASH' }])
    : undefined;

  // Skeleton form blocks instead of a full-screen backdrop while job config loads.
  if (isConfigLoading) {
    return (
      <Container size="lg">
        <PageHeader
          icon={<Radio size={22} />}
          title="Create Linear Job"
          description="Set up a 24/7 linear transcoding channel — stream inputs, output formats, protection and recording."
        />
        <div className={styles.skeletonGrid}>
          <Skeleton variant="rect" height={160} />
          <Skeleton variant="rect" height={160} />
          <Skeleton variant="rect" height={120} />
        </div>
      </Container>
    );
  }

  if (createdJob) {
    return (
      <Container size="lg">
        <PageHeader
          icon={<Radio size={22} />}
          title="Linear Job Created"
          description="Your 24/7 linear transcoding channel is ready — connect an encoder using the stream details below."
        />
        <ResultSection job={createdJob} title="Linear Job Created" />
        <ActionBar end={<Button onClick={() => navigate(`/services/live/${serviceId}/current-jobs`)}>Done</Button>} />
      </Container>
    );
  }

  // BasicSection expects CreateEventJobRequest shape; linear form is a superset so the cast is safe
  const basicForm = form as unknown as CreateEventJobRequest;

  return (
    <Container size="lg">
      <PageHeader
        icon={<Radio size={22} />}
        title="Create Linear Job"
        description="Set up a 24/7 linear transcoding channel — stream inputs, output formats, protection and recording."
      />

      {/* Basic: app_name, stream_name, timeshift, gpu flags */}
      <BasicSection
        form={basicForm}
        errors={{ app_name: errors.app_name, stream_name: errors.stream_name }}
        onChange={(field, value) => updateForm(field as keyof typeof form, value)}
        clearError={clearError}
        extraFlags={
          <>
            {/* Switch (not Checkbox) to match the Timeshift/GPU flags in the same row */}
            <label className={styles.switchItem}>
              <Switch
                checked={form.linear_config.dash_order_bitrate || false}
                onCheckedChange={(v) =>
                  updateLinearConfig({ ...form.linear_config, dash_order_bitrate: v === true })
                }
                id="dash-order-bitrate"
              />
              <span>Enable DASH Order Bitrate</span>
            </label>
            {canUseDashAdvancedTimeShift(form.input_protocol, form.outputs) && (
              <label className={styles.switchItem}>
                <Switch
                  checked={form.linear_config.dash_advanced_time_shifted_features || false}
                  onCheckedChange={(v) =>
                    updateLinearConfig({ ...form.linear_config, dash_advanced_time_shifted_features: v === true })
                  }
                  id="dash-advanced-timeshift"
                />
                <span>MPEG-DASH Advanced Time-Shifted Features</span>
              </label>
            )}
          </>
        }
      />

      {/* Stream config: extended input protocols + output/preset checkboxes */}
      <LinearStreamConfigSection
        form={form}
        audioOnlyFormat={audioOnlyFormat}
        onAudioOnlyFormatChange={setAudioOnlyFormat}
        configPresets={config?.presets}
        errors={{ outputs: errors.outputs, presets: errors.presets }}
        onChange={updateForm}
        clearError={clearError}
      />

      {/* Protocol-specific track / multicast / HA / DAI config */}
      <LinearConfigSection
        form={form}
        onChange={updateLinearConfig}
        errors={errors}
        clearError={clearError}
      />

      {/* HLS/DASH protection — pass verimatrixes from config */}
      <ProtectionSection
        outputs={form.outputs}
        hlsProtection={hlsProtection}
        dashProtection={dashProtection}
        verimatrixes={config?.verimatrixes}
        onHls={setHlsProtection}
        onDash={setDashProtection}
      />

      {/* Logo/watermark overlay */}
      <LogoWatermarkSection
        logo={logo}
        errors={errors}
        onChange={setLogo}
        clearError={clearError}
      />

      {/* Recording & snapshot — DASH mode forced for SRT/MPEGTS protocols */}
      <RecordingSnapshotSection
        recording={recording}
        snapshot={snapshot}
        config={config}
        recordingModes={recordingModes}
        onRecording={(patch) => setRecording((p) => ({ ...p, ...patch }))}
        onSnapshot={(patch) => setSnapshot((p) => ({ ...p, ...patch }))}
      />

      <ActionBar
        start={
          <Button variant="secondary" onClick={() => navigate(`/services/live/${serviceId}/current-jobs`)} disabled={isSubmitting}>
            Cancel
          </Button>
        }
        end={
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create Linear Job'}
          </Button>
        }
      />
    </Container>
  );
}
